/**
 * Token Refresh Service
 *
 * Handles refreshing expired wellness tokens using the refresh token
 */

import type { WellnessTokens } from '@/types/wellness';
import { useWellnessStore } from '@/stores/wellness-store';
import { getTokenExpiration, calculateExpirationTime } from '../utils/jwtUtils';

const API_URL = import.meta.env.VITE_REACT_APP_API_URL || '';

export interface RefreshTokenResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
  user: {
    email: string;
    firstName: string;
    lastName: string;
    birthDate: string;
    b2c: boolean;
    b2b2cSponsor?: string | null;
    needsProfile: boolean;
  };
}

/**
 * Refresh wellness tokens using refresh token
 * @param refreshToken - The refresh token to use
 * @returns New tokens with expiration times, or null if refresh failed
 */
export const refreshWellnessTokens = async (
  refreshToken: string
): Promise<WellnessTokens | null> => {
  try {
    const response = await fetch(`${API_URL}/self/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle refresh token expiration
      if (response.status === 401) {
        const code = errorData.code;
        if (
          code === 'REFRESH_TOKEN_EXPIRED' ||
          code === 'REFRESH_TOKEN_INVALID' ||
          code === 'REFRESH_FAILED'
        ) {
          console.warn('[tokenRefresh] Refresh token expired or invalid:', code);
          return null;
        }
      }
      
      throw new Error(
        errorData.message || `Token refresh failed: ${response.statusText}`
      );
    }

    const data: RefreshTokenResponse = await response.json();

    // Calculate expiration times
    const idTokenExpiration = getTokenExpiration(data.idToken);
    const idTokenExpiresAt = idTokenExpiration
      ? idTokenExpiration.getTime()
      : calculateExpirationTime(data.expiresIn);

    // Refresh token expiration (may not have exp claim, use expiresIn as fallback)
    const refreshTokenExpiration = getTokenExpiration(data.refreshToken);
    const refreshTokenExpiresAt = refreshTokenExpiration
      ? refreshTokenExpiration.getTime()
      : undefined; // Don't set if we can't determine expiration

    const newTokens: WellnessTokens = {
      accessToken: data.accessToken,
      idToken: data.idToken,
      refreshToken: data.refreshToken,
      idTokenExpiresAt,
      refreshTokenExpiresAt,
    };

    // Update store with new tokens and user info
    const { setAuthData } = useWellnessStore.getState();
    setAuthData({
      tokens: newTokens,
      user: data.user,
    });

    return newTokens;
  } catch (error) {
    console.error('[tokenRefresh] Error refreshing tokens:', error);
    return null;
  }
};



